import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ApiError, api, qs } from "../lib/api.ts";
import { useApi, relativeTime, titleCase } from "../lib/hooks.ts";
import { useAuth } from "../lib/auth.tsx";
import type { ActionRequest, Paged } from "../lib/types.ts";
import { Badge, Card, Empty, ErrorAlert, Pager, Spinner } from "../components/ui.tsx";

const STATUSES = ["pending", "running", "deferred", "completed", "failed", "cancelled"];
const TYPES = [
  "reboot", "freeze", "unfreeze", "wipe", "file-delete", "message", "script",
  "inventory-programs", "inventory-updates", "install-updates", "inventory-processes",
  "inventory-licenses", "inventory-health", "block-program", "get-blocklist",
  "uninstall-program", "backup", "bios_backup", "bios_revert", "self-update", "logo-install",
];

export function RequestsPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useAuth();
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const status = params.get("status") ?? "";
  const type = params.get("type") ?? "";
  const page = Number(params.get("page") ?? 1);

  function update(patch: Record<string, string | number | null>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value === "") next.delete(key);
      else next.set(key, String(value));
    }
    setParams(next, { replace: true });
  }

  const path = `/api/v3/requests${qs({ status, type, page, limit: 25 })}`;
  const requests = useApi<Paged<ActionRequest>>(path, [], { poll: 3000 });

  async function cancel(uid: string) {
    setError(null);
    setMessage(null);
    try {
      await api(`/api/v3/requests/${uid}/cancel`, { method: "POST" });
      setMessage(`Requisicao ${uid} cancelada.`);
      requests.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao cancelar");
      requests.reload();
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Requisicoes</h1>
          <p className="subtle">
            Fila de acoes remotas — pendentes sao processadas pelo worker e evoluem para
            execucao, conclusao ou falha.
          </p>
        </div>
        <span className="spacer" />
        <button className="btn" onClick={() => requests.reload()}>
          Atualizar
        </button>
      </div>

      {message ? <div className="alert info">{message}</div> : null}
      {error ? <ErrorAlert message={error} /> : null}

      <Card>
        <div className="toolbar">
          <select className="select" style={{ width: 200 }} value={status} onChange={(e) => update({ status: e.target.value, page: 1 })} aria-label="Status">
            <option value="">Todos os status</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {titleCase(s)}
              </option>
            ))}
          </select>
          <select className="select" style={{ width: 200 }} value={type} onChange={(e) => update({ type: e.target.value, page: 1 })} aria-label="Tipo">
            <option value="">Todos os tipos</option>
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {titleCase(t)}
              </option>
            ))}
          </select>
          {status || type ? (
            <button className="btn ghost" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
              Limpar filtros
            </button>
          ) : null}
          <span className="muted" style={{ marginLeft: "auto" }}>
            Atualizacao automatica a cada 3 segundos
          </span>
        </div>
      </Card>

      <Card tight>
        {requests.loading && !requests.data ? (
          <div className="empty"><Spinner /></div>
        ) : !requests.data || requests.data.data.length === 0 ? (
          <Empty>Nenhuma requisicao encontrada.</Empty>
        ) : (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Requisicao</th>
                    <th>Dispositivo</th>
                    <th>Status</th>
                    <th>Solicitante</th>
                    <th>Criada</th>
                    <th>Conclusao</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {requests.data.data.map((r) => (
                    <tr key={r.uid}>
                      <td>
                        <div className="cell-title">{titleCase(r.type)}</div>
                        <div className="cell-sub mono">{r.uid}</div>
                      </td>
                      <td>
                        <Link to={`/devices/${r.deviceUid}`}>{r.deviceName || r.deviceUid}</Link>
                      </td>
                      <td>
                        <Badge value={r.status} />
                        {r.error ? <div className="cell-sub">{r.error}</div> : null}
                      </td>
                      <td className="cell-sub">{r.requestedBy}</td>
                      <td className="cell-sub">{relativeTime(r.createdAt)}</td>
                      <td className="cell-sub">{r.finishedAt ? relativeTime(r.finishedAt) : "—"}</td>
                      <td>
                        {r.status === "pending" || r.status === "deferred" ? (
                          can("actions.issue") ? (
                            <button className="btn sm" onClick={() => cancel(r.uid)}>
                              Cancelar
                            </button>
                          ) : null
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager meta={requests.data.meta} page={page} onPage={(next) => update({ page: next })} />
          </>
        )}
      </Card>
    </div>
  );
}
